import React, { useState } from 'react';
import { Coins, MinusCircle } from 'lucide-react';
import { 
  rechargeStudentWallet, 
  batchRechargeSchool,
  type StudentWallet 
} from '../../../lib/apis_vibe';

interface RechargeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  target: { type: 'single'; user: StudentWallet } | { type: 'batch'; schoolId: number; schoolName: string };
  mode?: 'recharge' | 'deduct';
}

const RechargeModal: React.FC<RechargeModalProps> = ({ isOpen, onClose, onSuccess, target, mode = 'recharge' }) => {
  const [amount, setAmount] = useState<string>('1000');
  const [remark, setRemark] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const isBatch = target.type === 'batch';
  const isDeduct = mode === 'deduct';
  
  const title = isBatch 
    ? (isDeduct ? '全校批量扣减' : '全校批量充值') 
    : (isDeduct ? '学员单独扣减' : '学员单独充值');
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const val = parseInt(amount);
      if (isNaN(val) || val <= 0) {
        alert('请输入有效的正整数金额');
        return;
      }

      const actualAmount = isDeduct ? -val : val;

      if (isBatch) {
        await batchRechargeSchool({
          school_id: (target as any).schoolId,
          amount: actualAmount,
          remark: remark || (isDeduct ? '后台批量扣减' : '后台批量充值')
        });
        alert(`成功为该校学员${isDeduct ? '扣减' : '充值'} ${val} Token`);
      } else {
        await rechargeStudentWallet({
          user_id: (target as any).user.user_id,
          amount: actualAmount,
          remark: remark || (isDeduct ? '后台人工扣减' : '后台人工充值')
        });
        alert(`${isDeduct ? '扣减' : '充值'}成功`);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      alert(`${isDeduct ? '扣减' : '充值'}失败，请检查网络或权限`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 animate-in fade-in zoom-in duration-200">
        <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${isDeduct ? 'text-red-600' : 'text-gray-800'}`}>
          {isDeduct ? <MinusCircle className="text-red-500" /> : <Coins className="text-yellow-500" />}
          {title}
        </h2>
        
        {isBatch ? (
          <div className={`${isDeduct ? 'bg-red-50 text-red-800' : 'bg-yellow-50 text-yellow-800'} text-sm p-3 rounded-lg mb-4`}>
            正在为 <strong>{(target as any).schoolName}</strong> 下的所有学生{isDeduct ? '扣减' : '充值'}。<br/>
            此操作将生成多条流水记录。
          </div>
        ) : (
          <div className={`${isDeduct ? 'bg-red-50 text-red-800' : 'bg-blue-50 text-blue-800'} text-sm p-3 rounded-lg mb-4`}>
            正在为学员 <strong>{(target as any).user.username}</strong> {isDeduct ? '扣减' : '充值'}。
            <div className="flex gap-2 mt-1">
              <span className={`text-xs px-1.5 py-0.5 ${isDeduct ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'} rounded`}>{(target as any).user.role || '学员'}</span>
              {(target as any).user.memo && <span className={`text-xs px-1.5 py-0.5 ${isDeduct ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'} rounded`}>{(target as any).user.memo}</span>}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{isDeduct ? '扣减' : '充值'}数量 (Token)</label>
            <input 
              type="number" 
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="例如：1000"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">备注说明</label>
            <input 
              type="text" 
              value={remark}
              onChange={e => setRemark(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder={isBatch ? (isDeduct ? "违规处罚" : "开学季礼包") : (isDeduct ? "违规扣减" : "表现优异奖励")}
            />
          </div>
          <div className="flex gap-3 mt-6">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg">取消</button>
            <button 
              type="submit" 
              disabled={loading}
              className={`flex-1 px-4 py-2 ${isDeduct ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-lg disabled:opacity-50`}
            >
              {loading ? '提交中...' : `确认${isDeduct ? '扣减' : '充值'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RechargeModal;
